import backendUrl from "../../BackEndURL";

const VERSE_CASHE_VERSION = "v1";
const DB_NAME = "TamilHolyBibleDailyVerseCashe";
const DB_VERSION = 1;
const STORE_NAME = "dailyVerseCashe";

const DAILY_VERSES_LIST_KEY = `tamilHolyBible:dailyVerse:list:${VERSE_CASHE_VERSION}`;
const DAILY_VERSE_SELECTED_KEY = "dailyVerse";
const DAILY_VERSE_TIMESTAMP_KEY = "verseTimestamp";
const DAILY_VERSE_CACHE_TIME = 24 * 60 * 60 * 1000;

let indexedDbPromise = null;
let pendingDailyVersePromise = null;
let memoryDailyVerses = [];

const readJsonFromLocalStorage = (key) => {
  try {
    const value = localStorage.getItem(key);

    if (!value) {
      return null;
    }

    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const writeJsonToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore localStorage errors.
  }
};

const writeTextToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch (error) {
    // Ignore localStorage errors.
  }
};

const normalizeDailyVerses = (data) => {
  if (Array.isArray(data)) {
    return data.filter(Boolean);
  }

  if (Array.isArray(data?.dailyVerses)) {
    return data.dailyVerses.filter(Boolean);
  }

  if (Array.isArray(data?.verses)) {
    return data.verses.filter(Boolean);
  }

  if (Array.isArray(data?.data)) {
    return data.data.filter(Boolean);
  }

  return [];
};

const openDailyVerseIndexedDb = () => {
  if (typeof window === "undefined" || !window.indexedDB) {
    return Promise.resolve(null);
  }

  if (indexedDbPromise) {
    return indexedDbPromise;
  }

  indexedDbPromise = new Promise((resolve) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      resolve(null);
    };

    request.onblocked = () => {
      resolve(null);
    };
  });

  return indexedDbPromise;
};

const getFromIndexedDb = async (key) => {
  const db = await openDailyVerseIndexedDb();

  if (!db) {
    return null;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        resolve(null);
      };
    } catch (error) {
      resolve(null);
    }
  });
};

const setToIndexedDb = async (value) => {
  const db = await openDailyVerseIndexedDb();

  if (!db) {
    return false;
  }

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(value);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        resolve(false);
      };
    } catch (error) {
      resolve(false);
    }
  });
};

const readDailyVersesFromMemory = () => {
  if (Array.isArray(memoryDailyVerses) && memoryDailyVerses.length > 0) {
    return memoryDailyVerses;
  }

  return null;
};

const saveDailyVersesToMemory = (verses) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return;
  }

  memoryDailyVerses = verses;
};

const readDailyVersesFromLocalStorage = () => {
  const cachedData = readJsonFromLocalStorage(DAILY_VERSES_LIST_KEY);

  if (
    !cachedData ||
    cachedData.cacheVersion !== VERSE_CASHE_VERSION ||
    !Array.isArray(cachedData.verses) ||
    cachedData.verses.length === 0
  ) {
    return null;
  }

  saveDailyVersesToMemory(cachedData.verses);

  return cachedData.verses;
};

const saveDailyVersesToLocalStorage = (verses) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return;
  }

  writeJsonToLocalStorage(DAILY_VERSES_LIST_KEY, {
    cacheVersion: VERSE_CASHE_VERSION,
    savedAt: Date.now(),
    verses,
  });
};

const readDailyVersesFromIndexedDb = async () => {
  const cachedData = await getFromIndexedDb(DAILY_VERSES_LIST_KEY);

  if (
    !cachedData ||
    cachedData.cacheVersion !== VERSE_CASHE_VERSION ||
    !Array.isArray(cachedData.verses) ||
    cachedData.verses.length === 0
  ) {
    return null;
  }

  saveDailyVersesToMemory(cachedData.verses);
  saveDailyVersesToLocalStorage(cachedData.verses);

  return cachedData.verses;
};

const saveDailyVersesToIndexedDb = async (verses) => {
  if (!Array.isArray(verses) || verses.length === 0) {
    return false;
  }

  saveDailyVersesToMemory(verses);
  saveDailyVersesToLocalStorage(verses);

  return setToIndexedDb({
    id: DAILY_VERSES_LIST_KEY,
    cacheVersion: VERSE_CASHE_VERSION,
    savedAt: Date.now(),
    verses,
  });
};

const fetchDailyVersesFromApi = async () => {
  const response = await fetch(`${backendUrl}/api/dailyverses`);

  if (!response.ok) {
    throw new Error("Failed to fetch daily verses");
  }

  const data = await response.json();
  const verses = normalizeDailyVerses(data);

  if (verses.length > 0) {
    await saveDailyVersesToIndexedDb(verses);
  }

  return verses;
};

export const getCachedDailyVerses = async ({
  forceRefresh = false,
  allowNetwork = true,
} = {}) => {
  if (!forceRefresh) {
    const memoryVerses = readDailyVersesFromMemory();

    if (memoryVerses && memoryVerses.length > 0) {
      return memoryVerses;
    }

    const indexedDbVerses = await readDailyVersesFromIndexedDb();

    if (indexedDbVerses && indexedDbVerses.length > 0) {
      return indexedDbVerses;
    }

    const localStorageVerses = readDailyVersesFromLocalStorage();

    if (localStorageVerses && localStorageVerses.length > 0) {
      return localStorageVerses;
    }
  }

  if (!allowNetwork) {
    return [];
  }

  if (pendingDailyVersePromise) {
    return pendingDailyVersePromise;
  }

  pendingDailyVersePromise = fetchDailyVersesFromApi()
    .catch(() => [])
    .finally(() => {
      pendingDailyVersePromise = null;
    });

  return pendingDailyVersePromise;
};

export const startDailyVerseCache = () => {
  return getCachedDailyVerses({
    forceRefresh: false,
    allowNetwork: true,
  });
};

export const startDailyVerseCashe = startDailyVerseCache;

export const getDailyVerseForToday = async () => {
  const storedVerse = readJsonFromLocalStorage(DAILY_VERSE_SELECTED_KEY);
  const storedTimestamp = Number(
    localStorage.getItem(DAILY_VERSE_TIMESTAMP_KEY),
  );

  const selectedVerseIsValid =
    storedVerse &&
    storedTimestamp &&
    Date.now() - storedTimestamp < DAILY_VERSE_CACHE_TIME;

  if (selectedVerseIsValid) {
    return storedVerse;
  }

  const verses = await getCachedDailyVerses();

  if (!Array.isArray(verses) || verses.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * verses.length);
  const selectedVerse = verses[randomIndex];

  writeJsonToLocalStorage(DAILY_VERSE_SELECTED_KEY, selectedVerse);
  writeTextToLocalStorage(DAILY_VERSE_TIMESTAMP_KEY, Date.now());

  return selectedVerse;
};

export default startDailyVerseCache;
